import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GrowthRoadmap } from "@/pages/portal/GrowthRoadmap";
import BusinessPlanBuilder from "@/components/BusinessPlanBuilder";
import PortalGuide from "@/components/PortalGuide";
import { PromptDialog } from "@/components/PromptDialog";
import DesignStudio from "@/components/DesignStudio";
import ActivityTimeline from "@/components/ActivityTimeline";
import { CarePlans } from "@/components/CarePlans";
import { CheckoutPromo } from "@/components/CheckoutPromo";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { toast } from "sonner";
import {
  Loader2, LayoutDashboard, FolderKanban, Receipt, Download, MessageSquare, Upload,
  FileText, ArrowLeft, Send, Paperclip, CheckCircle2, RotateCcw, Clock, CreditCard,
  Home, LogOut, ChevronRight, ListChecks, Image as ImageIcon, Activity, ArrowRight,
  Copy, Key, History, ArrowUpCircle, ChevronDown, Package, Sparkles, TrendingUp, Bot, Megaphone, ShieldCheck, Landmark, AlertTriangle,
} from "lucide-react";

const STAGES = ["New Inquiry", "Discovery", "Proposal Sent", "Proposal Accepted", "Design", "Development", "Revision", "Testing", "Launch", "Completed"];

const NAV = [
  ["overview", "Overview", LayoutDashboard],
  ["dna", "Business Profile", Sparkles],
  ["plan", "Business Plan", FileText],
  ["roadmap", "Growth Roadmap", TrendingUp],
  ["funding", "Funding Readiness", Landmark],
  ["projects", "Projects", FolderKanban],
  ["invoices", "Invoices", Receipt],
  ["downloads", "Download Center", Download],
  ["mybot", "My AI Bot", Bot],
  ["activity", "Activity Timeline", Activity],
];

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("view");
    return ["overview", "dna", "plan", "roadmap", "funding", "projects", "invoices", "downloads", "mybot", "activity"].includes(v) ? v : "overview";
  });
  const [projects, setProjects] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [unread, setUnread] = useState({ total: 0, by_project: {} });
  const [annUnread, setAnnUnread] = useState(0);

  const loadUnread = useCallback(() => {
    api.get("/messages/unread").then((r) => setUnread(r.data)).catch(() => {});
    api.get("/portal/announcements/unread").then((r) => setAnnUnread(r.data.count || 0)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get("/projects/mine").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/invoices/mine").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/purchases/mine").then((r) => setPurchases(r.data)).catch(() => {});
    loadUnread();
  }, [loadUnread]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(loadUnread, 30000); return () => clearInterval(t); }, [loadUnread]);
  // Mark announcements seen once the client lands on the Overview.
  useEffect(() => {
    if (view === "overview" && annUnread > 0) {
      api.post("/portal/announcements/seen").then(() => setAnnUnread(0)).catch(() => {});
    }
  }, [view, annUnread]);

  const openProject = (id) => { setSelected(id); setView("projects"); };
  const dueTotal = invoices.filter((i) => i.status === "unpaid").reduce((s, i) => s + Number(i.amount), 0);

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex fixed left-0 h-screen w-64 bg-navy border-r border-white/10 flex-col z-40">
        <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-white/10">
          <div className="w-9 h-9 border border-gold flex items-center justify-center font-heading font-bold text-gold">ATF</div>
          <div><div className="font-heading text-white font-semibold leading-none">WebWorks</div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Client Portal</div></div>
        </Link>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => { setView(k); setSelected(null); }} data-testid={`portal-nav-${k}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${view === k && !selected ? "bg-gold/15 text-gold border-l-2 border-gold" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon size={17} /> {l}
              {k === "invoices" && dueTotal > 0 && <span className="ml-auto text-[10px] bg-gold text-navy px-2 py-0.5 font-bold">{invoices.filter(i => i.status === "unpaid").length}</span>}
              {k === "projects" && unread.total > 0 && <span className="ml-auto text-[10px] bg-gold text-navy px-2 py-0.5 font-bold" data-testid="nav-unread-badge">{unread.total}</span>}
              {k === "overview" && annUnread > 0 && <span className="ml-auto text-[9px] uppercase tracking-wider bg-gold text-navy px-2 py-0.5 font-bold" data-testid="nav-announcement-badge">New</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold text-sm font-semibold">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="min-w-0"><div className="text-white text-sm truncate">{user?.name}</div><div className="text-gray-500 text-xs truncate">{user?.email}</div></div>
          </div>
          <Link to="/" className="w-full flex items-center gap-3 px-2 py-2 text-sm text-gray-400 hover:text-white"><Home size={15} /> View Site</Link>
          <button onClick={logout} data-testid="portal-logout" className="w-full flex items-center gap-3 px-2 py-2 text-sm text-gray-400 hover:text-white"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 w-full">
        {/* Mobile nav */}
        <div className="lg:hidden flex gap-1 overflow-x-auto p-3 border-b border-white/10 bg-navy sticky top-0 z-40">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => { setView(k); setSelected(null); }} className={`relative flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap ${view === k && !selected ? "bg-gold text-navy" : "text-gray-400"}`}><Icon size={14} />{l}{k === "overview" && annUnread > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-gold" data-testid="nav-announcement-dot" />}</button>
          ))}
        </div>

        <div className="p-6 md:p-10 max-w-6xl">
          {selected ? (
            <ProjectDetail projectId={selected} onBack={() => { setSelected(null); load(); }} onRead={loadUnread} />
          ) : (
            <>
              <PortalGuide view={view} />
              {view === "overview" && <Overview user={user} projects={projects} invoices={invoices} dueTotal={dueTotal} onOpen={openProject} onGo={setView} unread={unread} />}
              {view === "dna" && <BusinessDNA />}
              {view === "plan" && <BusinessPlanBuilder mode="client" />}
              {view === "roadmap" && <GrowthRoadmap />}
              {view === "funding" && <FundingReadinessPanel />}
              {view === "projects" && <ProjectsList projects={projects} onOpen={openProject} unread={unread} />}
              {view === "invoices" && <Invoices invoices={invoices} reload={load} />}
              {view === "downloads" && <Downloads purchases={purchases} projects={projects} />}
              {view === "mybot" && <MyBot />}
              {view === "activity" && <ClientActivity />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BusinessDNA() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/portal/business-dna").then((r) => setD(r.data)).catch(() => setD({ loaded: false })).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader />;
  const score = d?.knowledge?.score || 0;
  const dims = d?.knowledge?.dimensions || [];
  const [levelName, levelNum] = score >= 100 ? ["Enterprise Ready", 5] : score >= 89 ? ["Professional", 4] : score >= 71 ? ["Established", 3] : score >= 52 ? ["Growing", 2] : ["Startup", 1];
  const missing = dims.filter((x) => x.stars === 0);
  const statusOf = (x) => (x.stars >= 4 ? "known" : x.stars >= 1 ? "partial" : "missing");
  const mark = { known: "✓", partial: "△", missing: "✕" };
  const cls = { known: "text-emerald-400 border-emerald-500/30 bg-emerald-500/[0.06]", partial: "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]", missing: "text-gray-500 border-white/10" };

  return (
    <div data-testid="portal-dna">
      <h1 className="font-heading text-3xl text-white mb-1 flex items-center gap-2"><Sparkles className="text-gold" size={24} /> <GlossaryTerm term="Business Profile">Business Profile</GlossaryTerm></h1>
      <p className="text-gray-400 mb-4">The more <GlossaryTerm term="Top Foster" className="text-gray-300">Top Foster</GlossaryTerm> knows about your business, the faster and smarter every website, proposal, and campaign becomes. <span className="text-white">Tell us once — we use it everywhere.</span></p>
      <div className="mb-8 flex items-start gap-2 text-sm text-emerald-400/90 bg-emerald-500/5 border border-emerald-500/20 px-4 py-3" data-testid="portal-human-review-note">
        <ShieldCheck size={15} className="shrink-0 mt-0.5" />
        <span className="text-emerald-300/90">Some details here are managed by our team to keep your plan accurate — and everything our AI drafts is reviewed by a real person before it's finalized or sent to you. Technology helps; our team always has the final say.</span>
      </div>

      {/* Knowledge score + level */}
      <div className="bg-navy-800 border border-gold/20 p-6 mb-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-gold mb-1">Knowledge Score</div>
            <div className="font-heading text-5xl text-white" data-testid="dna-score">{score}%</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Level {levelNum} of 5</div>
            <div className="font-heading text-2xl text-gold" data-testid="dna-level">{levelName}</div>
          </div>
        </div>
        <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
          <div className="h-full bg-gold transition-all duration-700" style={{ width: `${score}%` }} data-testid="dna-progress" />
        </div>
        <div className="flex justify-between text-[10px] text-gray-600 mt-1.5">
          <span>Startup 25%</span><span>Growing 52%</span><span>Established 71%</span><span>Professional 89%</span><span>Enterprise 100%</span>
        </div>
      </div>

      {/* DNA strands */}
      <h2 className="font-heading text-xl text-white mb-3">Your business details</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {dims.map((x) => {
          const s = statusOf(x);
          return (
            <div key={x.key} data-testid={`dna-strand-${x.key}`} className={`flex items-center justify-between gap-3 border p-3 ${cls[s]}`}>
              <div className="flex items-center gap-2.5">
                <span className="text-lg w-5 text-center">{mark[s]}</span>
                <div>
                  <div className="text-sm text-white">{x.label}</div>
                  {x.source && <div className="text-[11px] text-gray-500">From {x.source === "Questionnaire" ? "Questionnaire" : "Business Profile"}</div>}
                </div>
              </div>
              <div className="text-gold text-xs tracking-widest">{"★".repeat(x.stars)}<span className="text-gray-700">{"★".repeat(5 - x.stars)}</span></div>
            </div>
          );
        })}
      </div>

      {/* Design DNA & AI Homepage Concept */}
      <DesignStudio />

      {/* Missing knowledge + Complete action (placeholder) */}
      <div className="bg-navy-800 border border-white/10 p-6">
        <h2 className="font-heading text-xl text-white mb-2">Complete your Business Profile</h2>
        {missing.length ? (
          <>
            <p className="text-gray-400 mb-3">Top Foster still needs to learn {missing.length} thing{missing.length > 1 ? "s" : ""} to reach 100%. Adding your trust facts (licensed, insured, warranty, hours) directly sharpens your website and plan:</p>
            <div className="flex flex-wrap gap-2 mb-5" data-testid="dna-missing">
              {missing.map((m) => <span key={m.key} className="text-xs border border-white/15 text-gray-300 px-2.5 py-1 rounded-full">{m.label}</span>)}
            </div>
          </>
        ) : (
          <p className="text-emerald-400 mb-5">Your Business Profile is complete — Top Foster knows everything it needs. 🎉</p>
        )}
        <a
          data-testid="dna-complete-btn"
          href="/start"
          className={`inline-flex items-center gap-2 bg-gold text-navy font-medium px-5 py-2.5 hover:bg-gold/90 transition-colors ${!missing.length ? "opacity-50 pointer-events-none" : ""}`}>
          <Sparkles size={16} /> Complete My Business Profile
        </a>
      </div>
    </div>
  );
}

function ClientActivity() {
  return (
    <div data-testid="portal-activity">
      <h1 className="font-heading text-3xl text-white mb-1">Activity Timeline</h1>
      <p className="text-gray-400 mb-8">Your complete operational history — uploads, invoices, payments, projects, website builds, and more.</p>
      <div className="bg-navy-800 border border-white/5 p-6">
        <ActivityTimeline mode="portal" showFilters limit={50} testid="portal-activity-timeline"
          emptyText="No activity yet — your history will appear here as you work with us." />
      </div>
    </div>
  );
}

const FR_LEVEL_CLS = {
  "Lender Ready": "text-emerald-400 border-emerald-400/40 bg-emerald-500/10",
  "Nearly Ready": "text-gold border-gold/40 bg-gold/10",
  "Developing": "text-amber-400 border-amber-400/40 bg-amber-500/10",
  "Not Ready": "text-red-400 border-red-400/40 bg-red-500/10",
};

function FundingReadinessPanel() {
  const [checks, setChecks] = useState(null);
  useEffect(() => {
    api.get("/funding-readiness/mine").then((r) => setChecks(r.data.checks || [])).catch(() => setChecks([]));
  }, []);
  if (checks === null) return <Loader />;

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };
  const latest = checks[0];
  const first = checks[checks.length - 1];
  const improved = checks.length > 1 && latest.readiness_level !== first.readiness_level;

  return (
    <div data-testid="portal-funding">
      <h1 className="font-heading text-3xl text-white mb-1 flex items-center gap-2"><Landmark className="text-gold" size={24} /> Funding Readiness</h1>
      <p className="text-gray-400 mb-6">Each readiness check you save is tracked here, so you can watch your funding readiness improve as you close gaps. This is an educational estimate — ATF is not a lender and this is not a loan decision.</p>

      {checks.length === 0 ? (
        <div className="bg-navy-800 border border-white/10 p-8 text-center" data-testid="portal-funding-empty">
          <Landmark className="text-gold/60 mx-auto mb-3" size={32} />
          <h2 className="font-heading text-xl text-white mb-2">No saved checks yet</h2>
          <p className="text-gray-400 mb-5 max-w-md mx-auto">Run a funding readiness check and save it here to track your progress over time.</p>
          <a href="/funding-readiness" data-testid="portal-funding-start" className="inline-flex items-center gap-2 bg-gold text-navy font-medium px-5 py-2.5 hover:bg-gold/90 transition-colors">
            <ShieldCheck size={16} /> Check my funding readiness
          </a>
        </div>
      ) : (
        <>
          <div className="bg-navy-800 border border-gold/20 p-6 mb-6" data-testid="portal-funding-latest">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-gold mb-2">Latest check · {fmtDate(latest.created_at)}</div>
                <div className={`inline-flex items-center gap-2 px-4 py-2 border font-heading text-lg ${FR_LEVEL_CLS[latest.readiness_level] || "text-white border-white/20"}`} data-testid="portal-funding-level">
                  <ShieldCheck size={18} /> {latest.readiness_level}
                </div>
              </div>
              <div className="text-right text-sm text-gray-400">
                {latest.dscr != null && <div>DSCR <b className="text-gold">{Number(latest.dscr).toFixed(2)}×</b></div>}
                <div>{latest.gaps_count} gap{latest.gaps_count === 1 ? "" : "s"} to close</div>
              </div>
            </div>
            {improved && <p className="text-emerald-400 text-sm mt-4 flex items-center gap-2"><TrendingUp size={15} /> You've moved from "{first.readiness_level}" to "{latest.readiness_level}" — great progress.</p>}
          </div>

          <h2 className="font-heading text-xl text-white mb-3">History</h2>
          <div className="border border-white/10 divide-y divide-white/10 mb-6" data-testid="portal-funding-history">
            {checks.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`portal-funding-check-${c.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 border font-semibold ${FR_LEVEL_CLS[c.readiness_level] || "text-white border-white/20"}`}>{c.readiness_level}</span>
                  <span className="text-sm text-gray-400">{fmtDate(c.created_at)}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-4">
                  {c.dscr != null && <span>DSCR {Number(c.dscr).toFixed(2)}×</span>}
                  <span className="flex items-center gap-1"><AlertTriangle size={12} className="text-amber-400" /> {c.gaps_count}</span>
                </div>
              </div>
            ))}
          </div>

          <a href="/funding-readiness" data-testid="portal-funding-recheck" className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors">
            <ShieldCheck size={15} /> Run a new check
          </a>
        </>
      )}
    </div>
  );
}



function LaunchReadiness({ onGo }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [emailEdit, setEmailEdit] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const NAV_FOR = { business_plan: "plan", business_dna: "plan" };
  const MANUAL = ["domain", "google", "bing"];

  const load = useCallback(() => {
    api.get("/portal/readiness").then((r) => setData(r.data)).catch(() => setData({ overall: 0, items: [], next_steps: [], steps_to_go: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    const isDone = item.status === "done";
    setBusy(item.key);
    try {
      const { data: d } = await api.put(`/portal/readiness/${item.key}`, { done: !isDone });
      setData(d);
    } catch (e) { toast.error("Could not update"); } finally { setBusy(""); }
  };

  const saveEmail = async () => {
    setBusy("email");
    try {
      const { data: d } = await api.put("/portal/business-email", { email: emailVal.trim() });
      setData(d); setEmailEdit(false);
      toast.success(emailVal.trim() ? "Business email saved" : "Business email cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };

  if (!data) return null;
  const StatusIcon = ({ s }) => s === "done"
    ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
    : s === "in_progress"
      ? <Clock size={18} className="text-gold shrink-0" />
      : <span className="w-[18px] h-[18px] rounded-full border-2 border-gray-600 shrink-0 inline-block" />;

  return (
    <div className="bg-navy-800 border border-gold/25 p-6 mb-10" data-testid="launch-readiness">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gold mb-1">Business Readiness</div>
          <div className="flex items-baseline gap-3">
            <span className="font-heading text-4xl text-white" data-testid="readiness-overall">{data.overall}%</span>
            <span className="text-sm text-gray-400">
              {data.ready ? "You're ready to launch! 🚀" : data.steps_to_go > 0 ? `Ready in ${data.steps_to_go} step${data.steps_to_go === 1 ? "" : "s"}` : ""}
            </span>
          </div>
        </div>
        <div className="w-full sm:w-64">
          <div className="h-2 bg-white/10 overflow-hidden rounded-full">
            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${data.overall}%` }} />
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="readiness-items">
        {data.items.map((it) => {
          if (it.key === "email") {
            const editing = emailEdit;
            return (
              <div key="email" data-testid="readiness-item-email"
                className={`p-3 border ${it.status === "done" ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/10"}`}>
                <div className="flex items-center gap-3">
                  <StatusIcon s={it.status} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${it.status === "done" ? "text-gray-200" : "text-white"}`}>{it.label}</div>
                    {!editing && (
                      <button onClick={() => { setEmailVal(it.value || ""); setEmailEdit(true); }} data-testid="readiness-email-edit"
                        className="text-[11px] text-gold hover:text-gold-light truncate block max-w-full">
                        {it.value ? it.value : "Add your business email address"}
                      </button>
                    )}
                  </div>
                </div>
                {editing && (
                  <div className="flex gap-2 mt-2">
                    <input value={emailVal} onChange={(e) => setEmailVal(e.target.value)} placeholder="info@yourbusiness.com" type="email"
                      data-testid="readiness-email-input" autoFocus
                      className="flex-1 bg-navy border border-white/15 text-white px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                    <button onClick={saveEmail} disabled={busy === "email"} data-testid="readiness-email-save"
                      className="text-xs bg-gold text-navy px-3 py-1.5 font-semibold disabled:opacity-60">Save</button>
                    <button onClick={() => setEmailEdit(false)} className="text-xs text-gray-400 px-1">✕</button>
                  </div>
                )}
              </div>
            );
          }
          const manual = MANUAL.includes(it.key);
          const nav = NAV_FOR[it.key];
          const clickable = manual || nav;
          const onClick = () => { if (manual) toggle(it); else if (nav) onGo?.(nav); };
          return (
            <button key={it.key} onClick={clickable ? onClick : undefined} disabled={busy === it.key}
              data-testid={`readiness-item-${it.key}`}
              className={`flex items-center gap-3 p-3 border text-left transition-colors ${clickable ? "hover:border-gold/40 cursor-pointer" : "cursor-default"} ${it.status === "done" ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/10"}`}>
              <StatusIcon s={it.status} />
              <div className="min-w-0">
                <div className={`text-sm ${it.status === "done" ? "text-gray-200" : "text-white"}`}>{it.label}</div>
                {it.status !== "done" && <div className="text-[11px] text-gray-500 truncate">{manual ? "Tap to mark done" : it.hint}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PortalAnnouncements() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portal/announcements").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <div className="mb-8 space-y-3" data-testid="portal-announcements">
      {items.slice(0, 3).map((a) => (
        <div key={a.id} className="border border-gold/30 bg-gold/[0.06] p-5" data-testid={`portal-announcement-${a.id}`}>
          <div className="flex items-center gap-2 text-gold mb-1"><Megaphone size={16} /><span className="font-semibold text-white">{a.subject}</span>{a.is_new && <span className="text-[9px] uppercase tracking-wider bg-gold text-navy px-1.5 py-0.5 font-bold" data-testid={`announcement-new-${a.id}`}>New</span>}</div>
          <div className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{a.body}</div>
          <div className="text-[11px] text-gray-500 mt-2">{new Date(a.created_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}

function Overview({ user, projects, invoices, dueTotal, onOpen, onGo, unread = { by_project: {} } }) {
  if (projects === null) return <Loader />;
  const active = projects.filter((p) => p.stage !== "Completed").length;
  const unpaid = invoices.filter((i) => i.status === "unpaid").length;
  const cards = [
    { label: "Active Projects", value: active, icon: FolderKanban, go: "projects" },
    { label: "Amount Due", value: money(dueTotal), icon: CreditCard, go: "invoices" },
    { label: "Open Invoices", value: unpaid, icon: Receipt, go: "invoices" },
    { label: "Total Projects", value: projects.length, icon: Activity, go: "projects" },
  ];
  return (
    <div>
      <h1 className="font-heading text-3xl text-white mb-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
      <p className="text-gray-400 mb-8">Here's your path from idea to launch.</p>
      <PortalAnnouncements />
      <LaunchReadiness onGo={onGo} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <button key={c.label} onClick={() => onGo(c.go)} className="text-left bg-navy-800 border border-white/5 p-6 hover:border-gold/40 transition-colors" data-testid={`overview-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon className="text-gold mb-4" size={20} />
            <div className="font-heading text-3xl text-white">{c.value}</div>
            <div className="text-gray-500 text-sm mt-1">{c.label}</div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl text-white">Your projects</h2>
        <button onClick={() => onGo("projects")} className="text-gold text-sm flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight size={14} /></button>
      </div>
      {projects.length === 0 ? (
        <Empty title="No projects yet" cta={<Link to="/start" className="bg-gold text-navy px-6 py-3 font-semibold">Start a Project</Link>} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {projects.slice(0, 4).map((p) => <ProjectCard key={p.id} p={p} onOpen={onOpen} unread={unread.by_project?.[p.id]} />)}
        </div>
      )}
      <MyPlanCard />
      <ConciergeUpsell projects={projects} />
    </div>
  );
}

function ConciergeUpsell({ projects }) {
  const [busy, setBusy] = useState(false);
  const target = (projects || []).find((p) =>
    p.concierge?.status !== "Live" &&
    !(p.requested_addons || []).some((a) => String(a).includes("AI Concierge")));
  useEffect(() => {
    if (target) api.post("/concierge/track", { type: "view", project_id: target.id }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);
  if (!target) return null;
  const buy = async () => {
    setBusy(true);
    api.post("/concierge/track", { type: "click", project_id: target.id }).catch(() => {});
    try {
      const { data } = await api.post("/checkout/session", { kind: "concierge_bundle", project_id: target.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Could not start checkout — please try again."); setBusy(false); }
  };
  return (
    <div className="mt-10 border border-gold/30 bg-gold/5 p-6 flex items-start gap-4" data-testid="concierge-upsell">
      <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0"><Bot size={20} /></div>
      <div className="flex-1">
        <h3 className="font-heading text-lg text-white mb-1">Add a 24/7 AI Concierge to {target.org_name}</h3>
        <p className="text-gray-400 text-sm mb-3">Your own trained assistant answers customer questions and captures leads around the clock — day or night. Launch bundle: full setup + your first 3 months of managed care.</p>
        <button onClick={buy} disabled={busy} data-testid="concierge-upsell-request"
          className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} Get my AI Concierge — $697
        </button>
        <p className="text-[11px] text-gray-500 mt-2">Pay securely online now · setup + 3 months of care included · cancel care anytime after.</p>
      </div>
    </div>
  );
}

function MyPlanCard() {
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/subscriptions/mine").then((r) => setSubs(r.data.subscriptions || [])).catch(() => setSubs([])); }, []);
  if (subs === null) return null;
  const activeSub = subs.find((s) => s.status === "active") || subs.find((s) => s.status === "pending");
  return (
    <div className="mt-10" data-testid="my-plan-card">
      <h2 className="font-heading text-xl text-white mb-4">Your plan</h2>
      {activeSub ? (
        <div className="bg-navy-800 border border-gold/25 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-2xl text-white">{activeSub.plan_name} Plan</span>
              <span className={`text-[11px] px-2 py-0.5 border ${activeSub.status === "active" ? "text-green-300 border-green-500/30 bg-green-500/10" : "text-amber-300 border-amber-500/30 bg-amber-500/10"}`}>{activeSub.status}</span>
            </div>
            <div className="text-gray-400 text-sm mt-1">${activeSub.amount}/mo · {activeSub.next_billing_date ? `next invoice ${activeSub.next_billing_date}` : "awaiting first payment"}</div>
          </div>
          <Link to="/marketplace" className="text-gold text-sm flex items-center gap-1 hover:gap-2 transition-all">Manage / change plan <ArrowRight size={14} /></Link>
        </div>
      ) : (
        <div className="bg-navy-800 border border-white/10 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-heading text-lg text-white">Keep your site secure &amp; growing</div>
            <div className="text-gray-400 text-sm mt-1">Add a Hosting &amp; Care plan — hosting, security, backups and hands-on updates from $49/mo.</div>
          </div>
          <Link to="/marketplace" data-testid="browse-plans-cta" className="bg-gold text-navy px-5 py-2.5 font-semibold text-sm hover:bg-gold-light transition-colors whitespace-nowrap">View plans</Link>
        </div>
      )}
    </div>
  );
}

function ProjectsList({ projects, onOpen, unread = { by_project: {} } }) {
  if (projects === null) return <Loader />;
  return (
    <div>
      <h1 className="font-heading text-3xl text-white mb-1">Projects</h1>
      <p className="text-gray-400 mb-8">Track progress across all your engagements.</p>
      {projects.length === 0 ? (
        <Empty title="No projects yet" cta={<Link to="/start" className="bg-gold text-navy px-6 py-3 font-semibold">Start a Project</Link>} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="portal-projects">
          {projects.map((p) => <ProjectCard key={p.id} p={p} onOpen={onOpen} unread={unread.by_project?.[p.id]} />)}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ p, onOpen, unread }) {
  return (
    <button onClick={() => onOpen(p.id)} data-testid={`portal-project-${p.id}`} className="text-left glass p-6 hover:border-gold/40 transition-colors relative">
      {unread > 0 && (
        <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] bg-gold text-navy px-2 py-0.5 font-bold" data-testid={`project-unread-${p.id}`}>
          <MessageSquare size={11} /> {unread}
        </span>
      )}
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-heading text-xl text-white">{p.org_name}</h3>
        {!(unread > 0) && <StageBadge stage={p.stage} />}
      </div>
      <p className="text-gray-400 text-sm mb-4">{p.package_name || p.selected_design || "Custom website project"}</p>
      {p.concierge?.status === "Live" && (
        p.concierge?.demo_url ? (
          <span role="link" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); window.open(p.concierge.demo_url, "_blank", "noopener"); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.open(p.concierge.demo_url, "_blank", "noopener"); } }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-300 bg-green-500/10 border border-green-500/30 px-2 py-1 mb-3 cursor-pointer hover:bg-green-500/20 transition-colors"
            data-testid={`concierge-live-${p.id}`}>
            <Bot size={12} /> Your AI Concierge is live <ArrowRight size={11} />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-300 bg-green-500/10 border border-green-500/30 px-2 py-1 mb-3" data-testid={`concierge-live-${p.id}`}>
            <Bot size={12} /> Your AI Concierge is live
          </span>
        )
      )}
      {p.concierge?.status === "Live" && p.concierge?.questions_this_month > 0 && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-300 bg-navy border border-white/10 px-3 py-2" data-testid={`concierge-roi-${p.id}`}>
          <Bot size={13} className="text-gold shrink-0" />
          <span>Your AI Concierge answered <strong className="text-gold">{p.concierge.questions_this_month}</strong> customer question{p.concierge.questions_this_month === 1 ? "" : "s"} this month.</span>
        </div>
      )}
      <StageBar stage={p.stage} />
    </button>
  );
}

function Invoices({ invoices, reload }) {
  const [paying, setPaying] = useState(null);
  const [promos, setPromos] = useState({});
  const pay = async (inv) => {
    setPaying(inv.id);
    try {
      const { data } = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "invoice", invoice_id: inv.id, discount_code: promos[inv.id]?.code });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setPaying(null); }
  };
  return (
    <div>
      <h1 className="font-heading text-3xl text-white mb-1">Invoices</h1>
      <p className="text-gray-400 mb-8">Review and pay your invoices securely.</p>
      {invoices.length === 0 ? <Empty title="No invoices yet" desc="Invoices appear here once your proposal is sent." /> : (
        <div className="space-y-4" data-testid="portal-invoices">
          {invoices.map((inv) => {
            const promo = promos[inv.id];
            const finalAmt = promo ? Math.max(0, Math.round(inv.amount * (1 - promo.percent / 100) * 100) / 100) : inv.amount;
            const isFree = promo && promo.percent >= 100;
            return (
            <div key={inv.id} className="glass p-6" data-testid={`invoice-${inv.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-sm text-gold">{inv.number}</span>
                    <span className={`text-xs px-2.5 py-0.5 ${inv.status === "paid" ? "bg-green-500/15 text-green-400 border border-green-500/30" : "bg-gold/15 text-gold border border-gold/30"}`}>{inv.status === "paid" ? "Paid" : "Due"}</span>
                  </div>
                  <div className="text-white font-medium">{inv.description}</div>
                  <div className="text-gray-500 text-sm">{inv.org_name}</div>
                </div>
                <div className="flex items-center gap-5">
                  <div className="text-right">
                    <div className="font-heading text-2xl text-white">{money(finalAmt)}{promo ? <span className="text-gray-500 text-base line-through ml-2">{money(inv.amount)}</span> : null}</div>
                  </div>
                  {inv.status === "unpaid" ? (
                    <button onClick={() => pay(inv)} disabled={paying === inv.id} data-testid={`pay-invoice-${inv.id}`}
                      className="flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                      {paying === inv.id ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} {isFree ? "Get it free" : "Pay Now"}
                    </button>
                  ) : <span className="flex items-center gap-2 text-green-400 text-sm"><CheckCircle2 size={16} /> Paid</span>}
                </div>
              </div>
              {inv.status === "unpaid" && (
                <div className="mt-4 pt-4 border-t border-white/10" data-testid={`invoice-promo-${inv.id}`}>
                  <CheckoutPromo kind="invoice" applied={promo}
                    onApply={(d) => setPromos((p) => ({ ...p, [inv.id]: d }))}
                    onClear={() => setPromos((p) => { const n = { ...p }; delete n[inv.id]; return n; })} />
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

const RELEASE_STYLE = {
  major: "bg-gold/20 text-gold border-gold/40",
  minor: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  security: "bg-red-500/15 text-red-300 border-red-400/30",
};

function PurchaseCard({ o, onDownloaded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const versions = o.versions || [];

  const download = async (versionId, label) => {
    setBusy(true);
    try {
      const url = versionId ? `/templates/${o.template_id}/download?version_id=${versionId}` : `/templates/${o.template_id}/download`;
      const res = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = `${o.template?.slug || "template"}-v${label || o.current_version || "latest"}.zip`; a.click(); URL.revokeObjectURL(href);
      onDownloaded?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const copyLicense = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(o.license_key || "");
      } else {
        const ta = document.createElement("textarea");
        ta.value = o.license_key || ""; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("License key copied");
    } catch { toast.error("Couldn't copy — please copy the key manually"); }
  };

  return (
    <div className="glass p-5" data-testid={`purchase-${o.id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {o.template?.preview_image && <img src={o.template.preview_image} alt="" className="w-full md:w-28 h-24 md:h-20 object-cover shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-medium">{o.template_title}</h3>
            {o.current_version && <span className="text-[11px] text-gray-400 border border-white/15 px-2 py-0.5">v{o.current_version}</span>}
            {o.update_available && (
              <span className="text-[11px] bg-gold/20 text-gold border border-gold/40 px-2 py-0.5 flex items-center gap-1 font-medium" data-testid={`update-badge-${o.id}`}>
                <ArrowUpCircle size={12} /> Update available
              </span>
            )}
          </div>
          <div className="text-gray-500 text-sm mt-1">{money(o.amount)} · Paid{o.purchased_version ? ` · Bought v${o.purchased_version}` : ""}</div>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <Key size={13} className="text-gold" />
            <code className="text-gray-300 tracking-wide" data-testid={`license-${o.id}`}>{o.license_key || "—"}</code>
            {o.license_key && <button onClick={copyLicense} data-testid={`copy-license-${o.id}`} className="text-gray-500 hover:text-gold transition-colors"><Copy size={13} /></button>}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button disabled={busy} onClick={() => download(o.current_version_id, o.current_version)} data-testid={`template-download-${o.id}`}
            className="flex items-center justify-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Download latest
          </button>
          {versions.length > 0 && (
            <button onClick={() => setOpen((v) => !v)} data-testid={`version-history-toggle-${o.id}`}
              className="flex items-center justify-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors">
              <History size={14} /> Version history <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>

      {open && versions.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/10 space-y-3" data-testid={`versions-${o.id}`}>
          {versions.map((v) => (
            <div key={v.id} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white text-sm font-medium">v{v.version}</span>
                  <span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 ${RELEASE_STYLE[v.release_type] || RELEASE_STYLE.minor}`}>{v.release_type}</span>
                  <span className="text-gray-600 text-xs">{new Date(v.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{v.changelog}</p>
              </div>
              <button onClick={() => download(v.id, v.version)} data-testid={`download-version-${v.id}`}
                className="shrink-0 flex items-center gap-1.5 text-gold text-sm border border-gold/30 px-3 py-1.5 hover:bg-gold/10 transition-colors"><Download size={13} /> v{v.version}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Downloads({ purchases, projects }) {
  const [finals, setFinals] = useState([]);
  const [history, setHistory] = useState([]);
  const loadHistory = useCallback(() => api.get("/downloads/mine").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    if (!projects) return;
    (async () => {
      const all = [];
      for (const p of projects) {
        try {
          const { data } = await api.get(`/projects/${p.id}/files`);
          data.filter((f) => f.kind === "final").forEach((f) => all.push({ ...f, org: p.org_name }));
        } catch {}
      }
      setFinals(all);
    })();
  }, [projects]);

  const dl = async (fileId, filename) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const updates = purchases.filter((o) => o.update_available).length;

  return (
    <div>
      <h1 className="font-heading text-3xl text-white mb-1">Download Center</h1>
      <p className="text-gray-400 mb-8">Your completed website files, license keys, version history and downloads.</p>

      {updates > 0 && (
        <div className="glass p-4 mb-8 flex items-center gap-3 border-gold/30" data-testid="updates-banner">
          <ArrowUpCircle size={20} className="text-gold shrink-0" />
          <span className="text-gray-200 text-sm">{updates} of your website files {updates === 1 ? "has" : "have"} a newer version available. Download the latest below.</span>
        </div>
      )}

      <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><Package size={17} className="text-gold" /> Your website files</h2>
      {purchases.length === 0 ? <Empty title="No downloads yet" cta={<Link to="/marketplace" className="bg-gold text-navy px-6 py-3 font-semibold">See our work</Link>} /> : (
        <div className="space-y-4 mb-10" data-testid="portal-downloads">
          {purchases.map((o) => <PurchaseCard key={o.id} o={o} onDownloaded={loadHistory} />)}
        </div>
      )}

      <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><CheckCircle2 size={17} className="text-gold" /> Completed website files</h2>
      {finals.length === 0 ? <div className="glass p-8 text-gray-500 text-sm mb-10">Your finished website files will appear here once your project is delivered.</div> : (
        <div className="space-y-3 mb-10" data-testid="final-files">
          {finals.map((f) => (
            <div key={f.id} className="glass p-5 flex items-center justify-between">
              <div className="flex items-center gap-3 text-gray-200"><FileText size={18} className="text-gold" /> {f.filename} <span className="text-gray-500 text-sm">· {f.org}</span></div>
              <button onClick={() => dl(f.id, f.filename)} className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 hover:bg-gold/10 transition-colors"><Download size={16} /> Download</button>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><History size={17} className="text-gold" /> Download history</h2>
      {history.length === 0 ? <div className="glass p-8 text-gray-500 text-sm">Your downloads will be logged here.</div> : (
        <div className="glass divide-y divide-white/5" data-testid="download-history">
          {history.slice(0, 25).map((h) => (
            <div key={h.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-gray-200">{h.template_title} <span className="text-gray-500">· v{h.version}</span></span>
              <span className="text-gray-500 text-xs">{new Date(h.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectDetail({ projectId, onBack, onRead }) {
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [subtab, setSubtab] = useState("timeline");
  const [revisePrompt, setRevisePrompt] = useState(null);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [addonReqs, setAddonReqs] = useState([]);
  const [msg, setMsg] = useState("");
  const [paying, setPaying] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      if (data.proposal_id) api.get(`/proposals/${data.proposal_id}`).then((r) => setProposal(r.data)).catch(() => {});
    } catch { setProject(false); }
    api.get(`/projects/${projectId}/events`).then((r) => setEvents(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/messages`).then((r) => { setMessages(r.data); onRead?.(); }).catch(() => {});
    api.get(`/projects/${projectId}/files`).then((r) => setFiles(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/approvals`).then((r) => setApprovals(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/addon-requests`).then((r) => setAddonReqs(r.data)).catch(() => {});
  }, [projectId, onRead]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    try { const { data } = await api.post(`/projects/${projectId}/messages`, { text: msg }); setMessages((m) => [...m, data]); setMsg(""); onRead?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const downloadProposalPdf = async () => {
    try {
      const res = await api.get(`/proposals/${proposal.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${(project.org_name || "ATF").replace(/\s+/g, "-")}-proposal.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };
  const decideProposal = async (decision) => {
    try {
      await api.post(`/proposals/${proposal.id}/client-decision`, { decision });
      toast.success(decision === "accepted" ? "Proposal accepted — thank you! Next, complete your deposit to get started." : "Change request sent — we'll be in touch shortly.");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const dl = async (fileId, filename) => {
    try { const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error("Download failed"); }
  };
  const decide = async (id, decision, comment) => {
    try { await api.post(`/approvals/${id}/decision`, { decision, comment }); toast.success(decision === "approved" ? "Design approved" : "Revision requested"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const payInvoice = async (inv) => {
    setPaying(inv.id);
    try { const { data } = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "invoice", invoice_id: inv.id }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e)); setPaying(null); }
  };

  if (project === null) return <Loader />;
  if (project === false) return <div className="text-gray-400">Project not found. <button onClick={onBack} className="text-gold">Go back</button></div>;

  const req = project.request || {};
  const uploads = files.filter((f) => f.kind !== "final");
  const finals = files.filter((f) => f.kind === "final");
  const tabs = [
    ["timeline", "Timeline", Clock], ["approvals", "Approvals", ListChecks], ["messages", "Messages", MessageSquare],
    ["files", "Files", Upload], ["proposal", "Proposal", FileText], ["invoices", "Invoices", Receipt],
    ["addons", "Add-ons", Package],
  ];

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-gold mb-6" data-testid="project-back"><ArrowLeft size={16} /> All projects</button>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <h1 className="font-heading text-3xl text-white">{project.org_name}</h1>
        <StageBadge stage={project.stage} />
      </div>
      <p className="text-gray-400 mb-6">{project.package_name || project.selected_design || "Custom website project"}</p>
      <StageBar stage={project.stage} big />

      <div className="flex gap-2 border-b border-white/10 my-8 flex-wrap">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSubtab(k)} data-testid={`project-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${subtab === k ? "border-gold text-gold" : "border-transparent text-gray-400 hover:text-white"}`}>
            <Icon size={15} /> {l}
            {k === "approvals" && approvals.some((a) => a.status === "pending") && <span className="w-2 h-2 rounded-full bg-gold" />}
            {k === "invoices" && invoices.some((i) => i.status === "unpaid") && <span className="w-2 h-2 rounded-full bg-gold" />}
          </button>
        ))}
      </div>

      {subtab === "timeline" && (
        <div data-testid="project-timeline">
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Info label="Budget" value={project.budget || req.budget} />
            <Info label="Timeline" value={project.timeline || req.timeline} />
          </div>
          <h3 className="font-heading text-lg text-white mb-5">Project timeline</h3>
          {events.length === 0 ? <p className="text-gray-500">No activity yet.</p> : (
            <div className="relative pl-6 border-l border-white/10 space-y-6">
              {events.map((ev) => (
                <div key={ev.id} className="relative">
                  <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-gold ring-4 ring-navy" />
                  <div className="text-gray-200">{ev.text}</div>
                  <div className="text-gray-500 text-xs mt-1">{new Date(ev.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subtab === "approvals" && (
        <div data-testid="project-approvals">
          {approvals.length === 0 ? <Empty title="No designs to review yet" desc="When we submit a design for approval, it will appear here." /> : (
            <div className="space-y-6">
              {approvals.map((a) => (
                <div key={a.id} className="glass p-6" data-testid={`approval-${a.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-heading text-lg text-white">{a.title}</h4>
                    <span className={`text-xs px-2.5 py-0.5 ${a.status === "approved" ? "bg-green-500/15 text-green-400 border border-green-500/30" : a.status === "revision" ? "bg-orange-500/15 text-orange-400 border border-orange-500/30" : "bg-gold/15 text-gold border border-gold/30"}`}>{a.status}</span>
                  </div>
                  {a.image_url && <img src={a.image_url} alt={a.title} className="w-full max-h-96 object-cover border border-white/10 mb-4" />}
                  {a.note && <p className="text-gray-400 text-sm mb-4">{a.note}</p>}
                  {a.comment && <p className="text-orange-300 text-sm mb-4">Your feedback: {a.comment}</p>}
                  {a.status === "pending" && (
                    <div className="flex gap-3">
                      <button onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`} className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors"><CheckCircle2 size={16} /> Approve Design</button>
                      <button onClick={() => setRevisePrompt(a.id)} data-testid={`revise-${a.id}`} className="flex items-center gap-2 border border-white/25 text-white px-5 py-2.5 hover:bg-white/5 transition-colors"><RotateCcw size={16} /> Request Revisions</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <PromptDialog open={!!revisePrompt} title="Request revisions" label="What revisions would you like?"
        placeholder="Tell us what to change…" confirmText="Send request"
        onSubmit={(c) => { const id = revisePrompt; setRevisePrompt(null); decide(id, "revision", c); }}
        onClose={() => setRevisePrompt(null)} />

      {subtab === "messages" && (
        <div data-testid="project-messages">
          <div className="glass p-6 space-y-4 max-h-[420px] overflow-y-auto mb-4">
            {messages.length === 0 ? <p className="text-gray-500 text-center py-8">No messages yet. Say hello!</p> :
              messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] p-4 ${m.sender_role === "client" ? "bg-gold text-navy" : "bg-navy-700 text-gray-200"}`}>
                    <div className="text-xs opacity-70 mb-1">{m.sender_name} · {m.sender_role}</div>
                    <div className="text-sm">{m.text}</div>
                  </div>
                </div>
              ))}
          </div>
          <form onSubmit={sendMsg} className="flex gap-3">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" data-testid="message-input" className="flex-1 bg-navy border border-white/20 text-white p-3 focus:border-gold focus:outline-none" />
            <button type="submit" data-testid="message-send" className="bg-gold text-navy px-5 hover:bg-gold-light transition-colors"><Send size={18} /></button>
          </form>
        </div>
      )}

      {subtab === "files" && (
        <div data-testid="project-files">
          {finals.length > 0 && (
            <div className="mb-8">
              <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><CheckCircle2 size={17} className="text-gold" /> Completed website files</h3>
              <div className="space-y-3">
                {finals.map((f) => (
                  <div key={f.id} className="glass p-4 flex items-center justify-between border-gold/20">
                    <div className="flex items-center gap-3 text-gray-200"><FileText size={18} className="text-gold" /> {f.filename} <span className="text-gray-500 text-sm">({Math.round(f.size / 1024)} KB)</span></div>
                    <button onClick={() => dl(f.id, f.filename)} data-testid={`final-download-${f.id}`} className="text-gold hover:text-gold-light"><Download size={18} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <h3 className="font-heading text-lg text-white mb-4">Shared files</h3>
          <label className="glass border-dashed border-2 border-white/15 p-10 flex flex-col items-center justify-center cursor-pointer hover:border-gold/40 transition-colors mb-6">
            <Paperclip className="text-gold mb-3" size={24} />
            <span className="text-gray-300">Click to upload a file (max 8MB)</span>
            <input type="file" className="hidden" onChange={uploadFile} data-testid="file-upload-input" />
          </label>
          {uploads.length === 0 ? <p className="text-gray-500 text-center py-6">No files shared yet.</p> : (
            <div className="space-y-3">
              {uploads.map((f) => (
                <div key={f.id} className="glass p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-gray-200"><FileText size={18} className="text-gold" /> {f.filename} <span className="text-gray-500 text-sm">({Math.round(f.size / 1024)} KB) · {f.uploaded_by}</span></div>
                  <button onClick={() => dl(f.id, f.filename)} data-testid={`file-download-${f.id}`} className="text-gold hover:text-gold-light"><Download size={18} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subtab === "proposal" && (
        <div data-testid="project-proposal">
          {proposal ? (
            <div className="glass p-8">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h3 className="font-heading text-2xl text-white">{proposal.title}</h3>
                <div className="flex items-center gap-2">
                  <button onClick={downloadProposalPdf} data-testid="proposal-download-pdf" className="flex items-center gap-2 bg-gold text-navy text-sm px-4 py-2 font-semibold hover:bg-gold-light transition-colors"><Download size={15} /> Download PDF</button>
                  <button onClick={() => window.print()} className="text-gold text-sm border border-gold/40 px-4 py-2 hover:bg-gold/10 transition-colors">Print</button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-body text-gray-300 leading-relaxed text-sm">{proposal.proposal_text}</pre>
              {proposal.veteran_discount_percent > 0 && (
                <div className="mt-5 flex items-start gap-2.5 border border-gold/30 bg-gold/[0.07] px-4 py-3" data-testid="proposal-veteran-discount">
                  <ShieldCheck size={18} className="text-gold shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-200">Veteran appreciation discount: <span className="text-gold font-semibold">{proposal.veteran_discount_percent}% off</span> — applied to your investment (and your deposit) as thanks for your service.</p>
                </div>
              )}
              <div className="mt-6 flex items-center gap-4 border-t border-white/10 pt-6" data-testid="proposal-qr">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/proposal-qr.png`} alt="Scan QR code"
                  className="w-24 h-24 bg-white p-1.5 rounded"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                <div className="text-sm text-gray-400">Scan to explore pricing, portfolio &amp; book your consultation.</div>
              </div>
              {["draft", "sent"].includes(proposal.status) ? (
                <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="proposal-decision">
                  <button onClick={() => decideProposal("accepted")} data-testid="proposal-accept-btn"
                    className="flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
                    <CheckCircle2 size={16} /> Accept Proposal
                  </button>
                  <button onClick={() => decideProposal("rejected")} data-testid="proposal-request-changes-btn"
                    className="flex items-center gap-2 border border-white/25 text-white px-6 py-3 hover:bg-white/5 transition-colors">
                    Request Changes
                  </button>
                </div>
              ) : (
                <div className={`mt-6 inline-flex items-center gap-2 px-4 py-2.5 text-sm ${proposal.status === "accepted" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/25" : "bg-amber-400/10 text-amber-300 border border-amber-400/25"}`} data-testid="proposal-status-badge">
                  <CheckCircle2 size={15} /> Proposal {proposal.status === "accepted" ? "accepted" : proposal.status}
                </div>
              )}
              {proposal.status === "accepted" && invoices.some((i) => i.kind === "deposit" && i.status === "unpaid") && (
                <button onClick={() => { setSubtab("invoices"); }} data-testid="review-invoice-btn" className="mt-4 flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
                  <CheckCircle2 size={16} /> Pay Deposit to Get Started
                </button>
              )}
              {proposal.status === "accepted" && (
                <div className="mt-10 pt-8 border-t border-white/10" data-testid="proposal-care-plans">
                  <p className="text-gray-400 text-sm mb-5">Keep your new site secure, hosted and improving after launch — add a monthly plan (optional):</p>
                  <CarePlans projectId={projectId} compact />
                </div>
              )}
            </div>
          ) : <Empty title="No proposal yet" desc="We'll prepare your proposal soon. You'll see it here." />}
        </div>
      )}

      {subtab === "invoices" && (
        <div data-testid="project-invoices">
          {invoices.length === 0 ? <Empty title="No invoices yet" desc="Invoices appear here once your proposal is sent." /> : (
            <div className="space-y-4">
              {invoices.map((inv) => (
                <div key={inv.id} className="glass p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`project-invoice-${inv.id}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-sm text-gold">{inv.number}</span>
                      <span className={`text-xs px-2.5 py-0.5 ${inv.status === "paid" ? "bg-green-500/15 text-green-400 border border-green-500/30" : "bg-gold/15 text-gold border border-gold/30"}`}>{inv.status === "paid" ? "Paid" : "Due"}</span>
                    </div>
                    <div className="text-white font-medium">{inv.description}</div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="font-heading text-2xl text-white">{money(inv.amount)}</div>
                    {inv.status === "unpaid" ? (
                      <button onClick={() => payInvoice(inv)} disabled={paying === inv.id} data-testid={`pay-project-invoice-${inv.id}`} className="flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                        {paying === inv.id ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} Pay Now
                      </button>
                    ) : <span className="flex items-center gap-2 text-green-400 text-sm"><CheckCircle2 size={16} /> Paid</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subtab === "addons" && (
        <ProjectAddons projectId={projectId} existing={addonReqs} onDone={loadAll} />
      )}
    </div>
  );
}

function ProjectAddons({ projectId, existing, onDone }) {
  const [upgrades, setUpgrades] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/services-pricing").then((r) => setUpgrades(r.data.upgrades || [])).catch(() => {}); }, []);
  const toggle = (n) => setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const submit = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/addon-requests`, { addons: selected, note: note.trim() });
      toast.success("Request sent! We'll confirm scope and send you a quote.");
      setSelected([]); setNote("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const statusStyle = (s) => s === "approved" ? "bg-green-500/15 text-green-400 border-green-500/30"
    : s === "declined" ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-gold/15 text-gold border-gold/30";

  return (
    <div data-testid="project-addons" className="space-y-8">
      <div>
        <h3 className="font-heading text-xl text-white mb-1">Grow your site — add-ons & upgrades</h3>
        <p className="text-gray-400 text-sm mb-5">Select what you'd like to add. We'll confirm scope and send a quote — no charge until you approve.</p>
        <div className="border border-white/10 divide-y divide-white/10 max-w-2xl">
          {upgrades.map((u) => {
            const on = selected.includes(u.name);
            return (
              <button key={u.name} type="button" onClick={() => toggle(u.name)} data-testid={`portal-addon-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors ${on ? "bg-gold/10" : "hover:bg-white/[0.03]"}`}>
                <span className="flex items-center gap-3">
                  <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${on ? "bg-gold border-gold" : "border-white/30"}`}>
                    {on && <CheckCircle2 size={13} className="text-navy" />}
                  </span>
                  <span className={`text-sm ${on ? "text-white" : "text-gray-200"}`}>{u.name}</span>
                </span>
                <span className="text-gold font-semibold text-sm">{u.price}</span>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="max-w-2xl mt-4 space-y-3" data-testid="portal-addon-request-box">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything we should know? (optional)"
              className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-none" data-testid="portal-addon-note" />
            <button onClick={submit} disabled={busy} data-testid="portal-addon-submit"
              className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Request {selected.length} add-on{selected.length > 1 ? "s" : ""}
            </button>
          </div>
        )}
      </div>

      {existing?.length > 0 && (
        <div>
          <h4 className="font-heading text-lg text-white mb-3">Your requests</h4>
          <div className="space-y-3 max-w-2xl">
            {existing.map((r) => (
              <div key={r.id} className="glass p-4" data-testid={`portal-addon-request-${r.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm text-gray-300">{(r.addons || []).map((a) => a.name).join(", ")}</span>
                  <span className={`text-xs px-2.5 py-0.5 border capitalize ${statusStyle(r.status)}`}>{r.status}{r.invoice_number ? ` · ${r.invoice_number}` : ""}</span>
                </div>
                {r.note && <p className="text-xs text-gray-500">{r.note}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
const Loader = () => <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={28} /></div>;
const Empty = ({ title, desc, cta }) => (
  <div className="text-center py-16 glass" data-testid="empty-state">
    <FolderKanban className="text-gray-600 mx-auto mb-4" size={32} />
    <h3 className="font-heading text-xl text-white mb-2">{title}</h3>
    {desc && <p className="text-gray-500 mb-6 max-w-md mx-auto">{desc}</p>}
    {cta}
  </div>
);
const Info = ({ label, value }) => (
  <div className="glass p-4"><div className="text-xs uppercase tracking-wider text-gold mb-1">{label}</div><div className="text-gray-200">{value || "—"}</div></div>
);
function StageBadge({ stage }) {
  return <span className="text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1" data-testid="stage-badge">{stage}</span>;
}
function StageBar({ stage, big }) {
  const idx = STAGES.indexOf(stage);
  const pct = ((idx + 1) / STAGES.length) * 100;
  return (
    <div>
      <div className={`w-full bg-white/10 ${big ? "h-2" : "h-1.5"}`}><div className="h-full bg-gold transition-all duration-500" style={{ width: `${pct}%` }} /></div>
      {big && <div className="text-xs text-gray-500 mt-2">Stage {idx + 1} of {STAGES.length} · {stage}</div>}
    </div>
  );
}


function MyBot() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [leadsOpen, setLeadsOpen] = useState(null); // bot id
  const [leads, setLeads] = useState(null);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/portal/my-bots").then((r) => { setData(r.data.bots); setPlans(r.data.plans || []); }).catch(() => setData([]));
    api.get("/subscriptions/mine").then((r) => setSubs((r.data.subscriptions || []).filter((s) => String(s.plan_id).startsWith("bot_")))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeSub = subs.find((s) => s.status === "active");
  const pendingSub = subs.find((s) => s.status === "pending" || s.status === "past_due");

  const viewLeads = async (bot) => {
    if (leadsOpen === bot.id) { setLeadsOpen(null); return; }
    setLeadsOpen(bot.id); setLeads(null);
    try { const r = await api.get(`/portal/my-bots/${bot.id}/leads`); setLeads(r.data.leads); }
    catch (e) { toast.error(formatApiError(e)); setLeads([]); }
  };

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data: sub } = await api.post("/subscriptions/subscribe-bot", { plan_id: planId });
      if (sub.switched) { toast.success("Plan updated — your next invoice will reflect it."); load(); return; }
      const { data: co } = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "invoice", invoice_id: sub.invoice_id });
      window.location.href = co.url;
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };
  const cancelSub = async (sub) => {
    if (!window.confirm("Cancel your AI assistant plan? It stays active until the end of the current period.")) return;
    setBusy("cancel");
    try { await api.post(`/subscriptions/${sub.id}/cancel`); toast.success("Plan canceled."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  if (data === null) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

  return (
    <div data-testid="portal-mybot">
      <h1 className="text-2xl font-bold text-white mb-1">My AI Assistant</h1>
      <p className="text-gray-400 text-sm mb-8">Your branded chat assistant, its usage, and the leads it's captured for you.</p>

      {data.length === 0 ? (
        <div className="bg-navy-800 border border-white/10 p-8 text-center">
          <Bot size={30} className="text-gold mx-auto mb-3" />
          <div className="text-white font-semibold mb-1">No assistant on your account yet</div>
          <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">A 24/7 AI assistant answers your customers instantly and captures leads while you sleep. Explore plans or book a quick call.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/ai-chat-assistant" className="border border-white/20 text-white px-5 py-2.5 text-sm hover:bg-white/5">See plans</Link>
            <Link to="/book" className="bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold/90">Book a call</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {data.map((b) => {
            const u = b.usage || {};
            const pct = u.allowance ? Math.min(100, Math.round((u.messages / u.allowance) * 100)) : null;
            const link = `${process.env.REACT_APP_BACKEND_URL}/bot/${b.public_key}`;
            return (
              <div key={b.id} className="bg-navy-800 border border-white/10 p-6" data-testid={`mybot-card-${b.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: b.brand_color, color: "#0b1220" }}><Bot size={20} /></span>
                  <div>
                    <div className="text-white font-semibold">{b.name}</div>
                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-gold hover:underline flex items-center gap-1">Open assistant <ArrowRight size={11} /></a>
                  </div>
                  {!b.active && <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-300 border border-amber-400/30 px-2 py-0.5">paused</span>}
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                  <Stat label="Conversations (mo)" value={u.conversations ?? 0} />
                  <Stat label="Messages (mo)" value={u.messages ?? 0} />
                  <Stat label="Leads captured" value={u.leads ?? 0} />
                </div>
                {pct !== null && (
                  <div className="mb-4">
                    <div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>Monthly message allowance</span><span>{(u.messages ?? 0).toLocaleString()} / {u.allowance.toLocaleString()}</span></div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: u.over_allowance ? "#f87171" : u.near_limit ? "#fbbf24" : "#C9A227" }} /></div>
                  </div>
                )}
                <button onClick={() => viewLeads(b)} data-testid={`mybot-leads-${b.id}`} className="text-sm text-gold hover:underline">{leadsOpen === b.id ? "Hide leads" : `View leads (${u.leads ?? 0})`}</button>
                {leadsOpen === b.id && (
                  <div className="mt-3 space-y-2">
                    {leads === null ? <div className="text-gray-500 text-sm">Loading…</div> :
                      leads.length === 0 ? <div className="text-gray-500 text-sm">No leads yet.</div> :
                        leads.map((l) => (
                          <div key={l.id} className="bg-navy border border-white/10 p-3" data-testid={`mybot-lead-${l.id}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className="text-white text-sm font-medium">{l.name}</span>
                              <a href={`mailto:${l.email}`} className="text-xs text-gold hover:underline">{l.email}</a>
                            </div>
                            {l.message && <p className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">{l.message}</p>}
                            <div className="text-[10px] text-gray-500 mt-1">{new Date(l.created_at).toLocaleString()}</div>
                          </div>
                        ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Subscription */}
      {plans.length > 0 && (
        <div className="mt-10 bg-navy-800 border border-gold/20 p-6" data-testid="mybot-subscription">
          <div className="flex items-center gap-2 text-gold mb-1"><CreditCard size={18} /><span className="font-semibold text-white">Your plan</span></div>
          {activeSub ? (
            <p className="text-sm text-gray-300 mb-4">You're on <span className="text-white font-semibold">{activeSub.plan_name}</span> at {money(activeSub.amount)}/mo. Next invoice: {activeSub.next_billing_date || "—"}. Change tier below.</p>
          ) : pendingSub ? (
            <p className="text-sm text-amber-300 mb-4">Your {pendingSub.plan_name} subscription is awaiting first payment. Pick a plan below to pay and activate.</p>
          ) : (
            <p className="text-sm text-gray-300 mb-4">Choose a plan to keep your assistant running. You'll get a monthly invoice you can pay securely — cancel anytime.</p>
          )}
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map((p) => {
              const current = activeSub && activeSub.plan_id === `bot_${p.id}`;
              return (
                <div key={p.id} className={`border p-4 ${current ? "border-gold bg-gold/5" : "border-white/10"}`} data-testid={`mybot-plan-${p.id}`}>
                  <div className="text-gold text-xs uppercase tracking-widest mb-1">{p.name}</div>
                  <div className="text-2xl font-bold text-white">${p.price_usd}<span className="text-sm text-gray-400 font-normal">/mo</span></div>
                  <div className="text-xs text-gray-400 mb-4">{p.monthly_messages.toLocaleString()} messages / month</div>
                  <button disabled={busy === p.id || current} onClick={() => subscribe(p.id)} data-testid={`mybot-subscribe-${p.id}`}
                    className={`w-full py-2 text-sm font-semibold ${current ? "bg-white/10 text-gray-400 cursor-default" : "bg-gold text-navy hover:bg-gold/90"} disabled:opacity-60`}>
                    {busy === p.id ? "…" : current ? "Current plan" : activeSub ? "Switch to this" : "Choose & pay"}
                  </button>
                </div>
              );
            })}
          </div>
          {(activeSub || pendingSub) && (
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <p className="text-[11px] text-gray-500">Choose a lower tier above to downgrade — it applies from your next cycle.</p>
              <button onClick={() => cancelSub(activeSub || pendingSub)} disabled={busy === "cancel"} data-testid="mybot-cancel"
                className="text-xs text-red-300 hover:text-red-200 border border-red-400/20 px-3 py-1.5">
                {busy === "cancel" ? "Canceling…" : "Cancel plan"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-navy border border-white/10 p-3">
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-[11px] text-gray-500 uppercase tracking-wide mt-0.5">{label}</div>
  </div>
);
